import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { SECTION_ROUTES, SECTION_ALIASES } from './data/sectionRoutes'
import { SECTION_COMPONENT_PATHS } from './data/sectionComponentPaths'
import { ABROAD_ADMISSION_ROUTES } from './data/abroadAdmissionRoutes'
import { normalizePath } from './lib/seo'
import * as COURSE_ROUTE_DATA from './data/courseRoutes'
import Navbar from './components/Navbar/Navbar.jsx'
import Footer from './components/Footer/Footer.jsx'
import GlobalFloatingActions from './components/GlobalFloatingActions.jsx'

// Split heavy, route-specific modules out of the initial homepage bundle.
const HomePage = React.lazy(() => import('./pages/Home/Home'))
const Course = React.lazy(() => import('./pages/Courses/Course'))
const BlogDetail = React.lazy(() => import('./pages/Blog/BlogDetail'))
const BlogAdmin = React.lazy(() => import('./pages/Blog/BlogAdmin'))
const CollegeRouter = React.lazy(() => import('./CollegeRouter.jsx'))
const CutOffCalculatorPage = React.lazy(() => import('./pages/Tools/CutOff/CutOff.jsx'))
const AdmissionHelplinePage = React.lazy(() => import('./pages/Tools/AdmissionHelpline/AdmissionHelpline.jsx'))
const AboutPage = React.lazy(() => import('./pages/About/About.jsx'))

// Normalize lazy imports defensively. React.lazy requires the resolved module's
// `default` value to be a component, not a Promise or undefined. A few generated
// page modules can otherwise trigger production React error #306.
async function loadLazyComponent(importer, moduleLabel) {
  let loaded = await importer()
  let component = loaded?.default

  // Unwrap accidental Promise-valued default exports (including nested module
  // objects) instead of passing a Promise to React as an element type.
  for (let depth = 0; depth < 3 && component && typeof component.then === 'function'; depth += 1) {
    component = await component
    if (component && typeof component === 'object' && 'default' in component) component = component.default
  }

  const isComponent = typeof component === 'function' || (component && typeof component === 'object')
  if (isComponent) return { default: component }

  console.error(`[CollegeShine] Lazy module has no valid default component: ${moduleLabel}`, loaded)
  function InvalidLazyPage() {
    return (
      <main style={{ maxWidth: 760, margin: '48px auto', padding: 24, fontFamily: 'Arial,sans-serif' }}>
        <h1>Page temporarily unavailable</h1>
        <p>This page could not be loaded correctly. Please refresh the page or return to the course directory.</p>
        <p><a href="/course/">Browse courses</a></p>
      </main>
    )
  }
  return { default: InvalidLazyPage }
}


// CollegeRouter is imported synchronously on purpose. It is the boundary for the
// SQL-backed college directory/detail application; keeping it out of React.lazy
// prevents production error #306 if a stale/mismatched lazy chunk resolves without
// a default export. The individual content pages remain lazy-loaded below.

// IMPORTANT: page modules are lazy-loaded. The previous project eagerly imported
// hundreds of course/section pages, so an error in any unrelated page could make
// the Home page render as a blank screen. Home is now isolated from those pages.
const PAGE_MODULES = import.meta.glob('./pages/**/*.jsx')

const lazyCache = new Map()
function lazyPage(path) {
  // Resolve module keys case-insensitively as a safeguard for paths generated
  // on Windows and then served on case-sensitive environments (or vice versa).
  const requested = String(path || '').replace(/\\/g, '/')
  const resolvedPath = PAGE_MODULES[requested]
    ? requested
    : Object.keys(PAGE_MODULES).find((key) => key.toLowerCase() === requested.toLowerCase())
  if (!resolvedPath || !PAGE_MODULES[resolvedPath]) return null
  if (!lazyCache.has(resolvedPath)) lazyCache.set(resolvedPath, React.lazy(() => loadLazyComponent(PAGE_MODULES[resolvedPath], resolvedPath)))
  return lazyCache.get(resolvedPath)
}

const TOP_PAGE_PATHS = {
  '/about/': './pages/About/About.jsx',
  '/contact/': './pages/Contact/Contact.jsx',
  '/career/': './pages/Career/Career.jsx',
  '/advertisement/': './pages/Advertisement/Advertisement.jsx',
  '/partner-with-us/': './pages/PartnerWithUs/PartnerWithUs.jsx',
  '/privacy-policy/': './pages/Legal/PrivacyPolicy/PrivacyPolicy.jsx',
  '/terms-and-conditions/': './pages/Legal/TermsAndConditions/TermsAndConditions.jsx',
  // Legacy URL used by existing footer links and indexed site inventory.
  '/terms-condition/': './pages/Legal/TermsAndConditions/TermsAndConditions.jsx',
  '/sitemap/': './pages/Legal/Sitemap/Sitemap.jsx',
  '/blog/': './pages/Blog/Blog.jsx',
}

const ABROAD_PAGE_PATHS = {
  '/services/abroad-admission/': './pages/Services/AbroadAdmission/AbroadAdmission.jsx',
  '/services/abroad-admission/france/': './pages/Services/AbroadAdmission/France/France.jsx',
  '/services/abroad-admission/germany/': './pages/Services/AbroadAdmission/Germany/Germany.jsx',
  '/services/abroad-admission/australia/': './pages/Services/AbroadAdmission/Australia/Australia.jsx',
  '/services/abroad-admission/uk/': './pages/Services/AbroadAdmission/UK/UK.jsx',
  '/services/abroad-admission/ireland/': './pages/Services/AbroadAdmission/Ireland/Ireland.jsx',
  '/services/abroad-admission/russia/': './pages/Services/AbroadAdmission/Russia/Russia.jsx',
}

function sectionPagePath(sectionKey, componentName) {
  return SECTION_COMPONENT_PATHS[`${sectionKey}:${componentName}`] || `./pages/${sectionKey}/${componentName}/${componentName}.jsx`
}

function sectionHubPath(sectionKey) {
  return `./pages/${sectionKey}/${sectionKey}.jsx`
}

function NotFound() {
  useEffect(() => {
    document.title = 'Page Not Found | CollegeShine'
    const robots = document.head.querySelector('meta[name="robots"]')
    if (robots) robots.setAttribute('content', 'noindex,follow')
  }, [])
  return (
    <main style={{ minHeight: '45vh', display: 'grid', placeItems: 'center', padding: 40 }}>
      <div><h1>Page Not Found</h1><p>The requested CollegeShine page could not be found.</p></div>
    </main>
  )
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error) {
    return { error }
  }
  render() {
    if (!this.state.error) return this.props.children
    return (
      <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 32, fontFamily: 'Arial,sans-serif', background: '#f8fafc' }}>
        <div style={{ maxWidth: 760, width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28, boxShadow: '0 12px 40px rgba(0,0,0,.08)' }}>
          <h1 style={{ marginTop: 0 }}>CollegeShine page error</h1>
          <p>The page could not be rendered. The exact error is shown below so it can be fixed instead of displaying a blank screen.</p>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: '#f3f4f6', padding: 16, borderRadius: 10 }}>{String(this.state.error?.stack || this.state.error)}</pre>
        </div>
      </main>
    )
  }
}

function Loading() {
  return <div style={{ minHeight: '30vh', display: 'grid', placeItems: 'center', fontFamily: 'Arial,sans-serif' }}>Loading CollegeShine…</div>
}

function RenderLazy({ path }) {
  // Render the Admission Helpline and Cut Off Calculator eagerly. Their lazy
  // Suspense fallbacks were replaced by full page content after chunk loading,
  // creating a large mobile layout shift. Keep unrelated routes lazy.
  const normalizedPath = String(path || window.location.pathname).toLowerCase().replace(/\/+$/, '')
  if (normalizedPath.endsWith('/pages/about/about.jsx') || normalizedPath === '/about') return <Suspense fallback={<Loading />}><AboutPage /></Suspense>
  if (normalizedPath === '/tools/admission-helpline') {
    return <Suspense fallback={<Loading />}><AdmissionHelplinePage /></Suspense>
  }
  if (
    normalizedPath === '/tools/cut-off-calculator-tool' ||
    normalizedPath === '/tools/collegeshine-com-cut-off-calculator-tamil-nadu'
  ) {
    return <Suspense fallback={<Loading />}><CutOffCalculatorPage /></Suspense>
  }

  const Component = lazyPage(path)
  if (!Component) return <NotFound />
  return <Suspense fallback={<Loading />}><Component /></Suspense>
}

function findRoute(path, routeData = {}) {
  // Normalize URL encoding and letter case consistently for local and production.
  let normalizedPath = String(path || '/')
  try { normalizedPath = decodeURI(normalizedPath) } catch {}
  normalizedPath = normalizedPath.toLowerCase()
  path = normalizedPath
  const COURSE_CATEGORIES = routeData.COURSE_CATEGORIES || []
  const COURSE_ROUTES = routeData.COURSE_ROUTES || []
  const COURSE_ALIASES = routeData.COURSE_ALIASES || []
  // The SQL-backed college directory is mounted inside the main CollegeShine
  // application without replacing the existing blog/course page architecture.
  if (path === '/colleges/' || path.startsWith('/colleges/') || path === '/districts/' || path.startsWith('/districts/') || path === '/search/' || path.startsWith('/search/')) {
    return { type: 'collegeApp' }
  }
  if (path === '/') return { type: 'home' }

  if (path === '/admin/blogs/') return { type: 'blogAdmin' }
  if (path.startsWith('/blog/') && path !== '/blog/') return { type: 'blogDetail', slug: path.slice('/blog/'.length).replace(/\/$/, '') }
  if (TOP_PAGE_PATHS[path]) return { type: 'topPage', path: TOP_PAGE_PATHS[path] }
  if (path === '/course/') return { type: 'coursesHub' }

  if (path === ABROAD_ADMISSION_ROUTES.path) return { type: 'abroadHub', path: ABROAD_PAGE_PATHS[path] }
  const abroadCountry = ABROAD_ADMISSION_ROUTES.links.find(item => item.path === path)
  if (abroadCountry) return { type: 'abroadCountry', path: ABROAD_PAGE_PATHS[path] }


  const category = COURSE_CATEGORIES.find(item => item.path === path)
  if (category) return { type: 'coursePage', path: category.component }

  const course = COURSE_ROUTES.find(item => item.path === path)
  if (course) return { type: 'coursePage', path: course.component }

  const alias = COURSE_ALIASES.find(item => item.path === path)
  if (alias) {
    const target = COURSE_ROUTES.find(item => item.path === alias.target)
    if (target) return { type: 'coursePage', path: target.component, alias: true }
  }

  for (const section of SECTION_ROUTES) {
    if (section.path === path) return { type: 'sectionHub', path: sectionHubPath(section.key) }
    const child = section.links.find(item => item.path === path)
    if (child) return { type: 'sectionPage', path: sectionPagePath(section.key, child.component) }
  }

  const sectionAlias = SECTION_ALIASES.find(item => item.path === path)
  if (sectionAlias) {
    const section = SECTION_ROUTES.find(item => item.path === sectionAlias.target)
    if (section) return { type: 'sectionHub', path: sectionHubPath(section.key), alias: true }
  }

  return null
}

function AppContent() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname))
  const [collegeRouteReady, setCollegeRouteReady] = useState(() => { const initialPath = normalizePath(window.location.pathname); return !(/^\/colleges\/[^/]+\/?$/.test(initialPath) || initialPath === '/colleges/') })
  useEffect(() => {
    const onPop = () => setPath(normalizePath(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    const isPotentialCollegeDetail = /^\/colleges\/[^/]+\/?$/.test(path)
    const isDirectoryIndex = path === '/colleges/'
    setCollegeRouteReady(!(isPotentialCollegeDetail || isDirectoryIndex))
    if (isDirectoryIndex) {
      const onDirectoryReady = () => setCollegeRouteReady(true)
      window.addEventListener('collegeshine:directory-ready', onDirectoryReady, { once: true })
      return () => window.removeEventListener('collegeshine:directory-ready', onDirectoryReady)
    }
    if (!isPotentialCollegeDetail) return undefined
    const onCollegeReady = () => setCollegeRouteReady(true)
    const onDirectoryReady = () => setCollegeRouteReady(true)
    window.addEventListener('collegeshine:college-detail-ready', onCollegeReady, { once: true })
    window.addEventListener('collegeshine:directory-ready', onDirectoryReady, { once: true })
    if (document.documentElement.dataset.collegeDetailReady === 'true') setCollegeRouteReady(true)
    return () => {
      window.removeEventListener('collegeshine:college-detail-ready', onCollegeReady)
      window.removeEventListener('collegeshine:directory-ready', onDirectoryReady)
    }
  }, [path])

  useEffect(() => {
    const updateMainLandmark = () => {
      const main = document.querySelector('main')
      if (main && !main.id) main.id = 'main-content'
    }
    const frame = window.requestAnimationFrame(updateMainLandmark)
    return () => window.cancelAnimationFrame(frame)
  }, [path])

  // Keep course route metadata available synchronously in development and in
  // production builds. A deferred route-table import could briefly resolve a
  // valid deep link as Not Found on local setups using non-standard serving.
  const route = useMemo(() => findRoute(path, COURSE_ROUTE_DATA), [path])
  let pageContent

  if (!route) {
    pageContent = <NotFound />
  } else if (route.type === 'collegeApp') {
    pageContent = <Suspense fallback={<Loading />}><CollegeRouter /></Suspense>
  } else if (route.type === 'redirect') {
    window.history.replaceState({}, '', route.target)
    queueMicrotask(() => setPath(route.target))
    pageContent = <Loading />
  } else if (route.type === 'home') {
    pageContent = <Suspense fallback={<Loading />}><HomePage /></Suspense>
  } else if (route.type === 'blogDetail') {
    pageContent = <Suspense fallback={<Loading />}><BlogDetail slug={route.slug} /></Suspense>
  } else if (route.type === 'blogAdmin') {
    pageContent = <Suspense fallback={<Loading />}><BlogAdmin /></Suspense>
  } else if (route.type === 'coursesHub') {
    pageContent = <Suspense fallback={<Loading />}><Course /></Suspense>
  } else {
    pageContent = <RenderLazy path={route.path} />
  }

  // Services pages share the application Navbar and Footer. Keep them in one
  // place so nested service pages never render duplicate headers or footers.
  const currentRoutePath = String(route?.path || window.location.pathname).toLowerCase();
  // College directory and individual college detail pages already provide their own WhatsApp and back-to-top controls. Keep the global floating actions off these routes only.
  const isCollegeDirectoryOrDetail = /^\/colleges\/(?:[^/]+\/?)?$/.test(path);
  const isServicesRoute = currentRoutePath.startsWith('/services');
  const isCareerToolRoute = currentRoutePath.startsWith('/tools/career-path-generator') || currentRoutePath.startsWith('/career-path-generator-tool');
  const isSkillToolRoute = currentRoutePath.startsWith('/tools/skill-recommentation-tool') || currentRoutePath.startsWith('/skill-recommendation-tool');
  const isScholarshipToolRoute = currentRoutePath.startsWith('/tools/scholarship-finder-tool') || currentRoutePath.startsWith('/scholarship-finder-tool');
  const isAdmissionHelplineRoute = window.location.pathname.toLowerCase().startsWith('/tools/admission-helpline');
  const isAboutRoute = window.location.pathname.toLowerCase().replace(/\/+$/, '') === '/about';
  const isContactRoute = window.location.pathname.toLowerCase().replace(/\/+$/, '') === '/contact';
  const isAdvertisementRoute = currentRoutePath.replace(/\/+$/, '') === '/advertisement';
  const isCareerPageRoute = currentRoutePath.replace(/\/+$/, '') === '/career';
  const isPartnerWithUsRoute = currentRoutePath.replace(/\/+$/, '') === '/partner-with-us';
  // Keep the cutoff tool in the same stable shared shell as the other tools.
  // Previously it rendered as a bare main landmark and the page could jump
  // vertically as the route shell/styles settled during mobile Lighthouse runs.
  const isCutoffToolRoute = currentRoutePath.startsWith('/tools/cut-off-calculator-tool') || currentRoutePath.startsWith('/tools/collegeshine-com-cut-off-calculator-tamil-nadu');
  const isCareerToolsLayout = isCareerToolRoute || isSkillToolRoute || isScholarshipToolRoute || isCutoffToolRoute;
  if (route?.type === 'collegeApp' || isServicesRoute || isCareerToolsLayout || isAdmissionHelplineRoute || isAboutRoute || isContactRoute || isAdvertisementRoute || isCareerPageRoute || isPartnerWithUsRoute) {
    return (
      <>
        <Navbar />
        <div className={route?.type === 'collegeApp' ? "college-app-root" : "app-page-root"}>{pageContent}</div>
        {(collegeRouteReady || isCareerToolsLayout || isAdmissionHelplineRoute || isAboutRoute || isContactRoute || isAdvertisementRoute || isCareerPageRoute || isPartnerWithUsRoute) && <Footer />}
        {!isCollegeDirectoryOrDetail && <GlobalFloatingActions />}
      </>
    )
  }

  return <>{pageContent}{!isCollegeDirectoryOrDetail && <GlobalFloatingActions />}</>
}

export default function App() {
  return <AppErrorBoundary><AppContent /></AppErrorBoundary>
}
